import { useEffect, useState } from 'react';
import {
  getPwaUpdateSnapshot,
  requestPwaUpdateCheck,
  subscribePwaUpdate,
  type PwaUpdateCheckResult,
} from '../pwa';
import { APP_VERSION } from '../lib/appVersion';

type CheckState = 'idle' | 'checking' | PwaUpdateCheckResult;

export function AppUpdatesSettings(): JSX.Element {
  const [state, setState] = useState<CheckState>(
    getPwaUpdateSnapshot() ? 'update-available' : 'idle',
  );

  useEffect(() => subscribePwaUpdate(() => {
    if (getPwaUpdateSnapshot()) setState('update-available');
  }), []);

  const check = async (): Promise<void> => {
    if (state === 'checking') return;
    setState('checking');
    const result = await requestPwaUpdateCheck();
    setState(result);
  };

  let message: string | null = null;
  if (state === 'checking') message = 'Checking for updates…';
  else if (state === 'up-to-date') message = 'Conscious Ritual is up to date.';
  else if (state === 'offline') message = 'Connect to the internet to check for updates.';
  else if (state === 'failed') message = 'Could not check for updates. Try again later.';
  else if (state === 'unsupported') message = 'Update checks are unavailable in this environment.';
  else if (state === 'update-available') {
    message = 'An update is ready. Use the update notice above when you are ready to apply it.';
  }

  return (
    <section className="card stack settings-grouped" aria-labelledby="app-updates-title">
      <h2 id="app-updates-title" className="mt-0">App Updates</h2>
      <p className="page-lead mt-0">Keep Conscious Ritual current without changing your personal data.</p>
      <p className="hint" aria-label={`Current version ${APP_VERSION}`}>
        Current Version: <span className="mono">{APP_VERSION}</span>
      </p>
      <button type="button" className="btn btn-secondary" disabled={state === 'checking'} onClick={() => void check()}>
        {state === 'checking' ? 'Checking…' : 'Check for Updates'}
      </button>
      {message ? <p className="hint" role="status" aria-live="polite">{message}</p> : null}
    </section>
  );
}
